import type { TesseraLaneBOverlayId } from "../../types/executionCandlesOverlays";
import type { ReportPick, ReportPickSliceContext } from "../reportPickListStorage";

const METRIC_TO_LANE_B: Array<{ pattern: RegExp; ids: TesseraLaneBOverlayId[] }> = [
  { pattern: /\b(ma50|sma50)\b/i, ids: ["sma50"] },
  { pattern: /\b(ma200|sma200)\b/i, ids: ["sma200"] },
  { pattern: /\bbollinger|\bbb_zone|\bbb_quartile|\bsqueeze\b/i, ids: ["bb", "bb_zone", "bb_quartile", "squeeze"] },
  { pattern: /\bbb\b/i, ids: ["bb"] },
  { pattern: /\bsqueeze\b/i, ids: ["squeeze", "bb"] },
  { pattern: /\bbb_zone\b/i, ids: ["bb_zone", "bb"] },
  { pattern: /\bbb_quartile\b/i, ids: ["bb_quartile", "bb"] },
  { pattern: /\badx_bucket\b/i, ids: ["adx_bucket", "adx"] },
  { pattern: /\badx\b/i, ids: ["adx"] },
  { pattern: /\brsi_bucket\b/i, ids: ["rsi_bucket", "rsi"] },
  { pattern: /\brsi\b/i, ids: ["rsi"] },
  { pattern: /\batr_regime\b/i, ids: ["atr_regime", "atr"] },
  { pattern: /\batr\b/i, ids: ["atr"] },
  { pattern: /\b(sr|support|resistance|room)\b/i, ids: ["sr"] },
  { pattern: /\b(regime|market_condition|uptrend|downtrend|choppy|range)\b/i, ids: ["regime"] },
  { pattern: /\bdirection_short\b/i, ids: ["direction_short"] },
  { pattern: /\bdirection\b/i, ids: ["direction"] },
  { pattern: /\bsession\b/i, ids: ["session"] },
];

function addFromText(text: string, into: Set<TesseraLaneBOverlayId>): void {
  const t = text.trim();
  if (!t) return;
  for (const { pattern, ids } of METRIC_TO_LANE_B) {
    if (pattern.test(t)) {
      for (const id of ids) into.add(id);
    }
  }
}

function addFromSliceContext(
  ctx: ReportPickSliceContext | null | undefined,
  into: Set<TesseraLaneBOverlayId>
): void {
  if (!ctx) return;
  if (ctx.market_condition) into.add("regime");
  if (ctx.candle_direction) {
    into.add(ctx.triple_candle_window === "short" ? "direction_short" : "direction");
  }
  if (ctx.session_id) into.add("session");
  addFromText(ctx.label, into);
  addFromText(ctx.sliceId, into);
  addFromText(ctx.kind, into);
}

function walkUnknown(node: unknown, into: Set<TesseraLaneBOverlayId>, depth = 0): void {
  if (depth > 8 || node == null) return;
  if (typeof node === "string") {
    addFromText(node, into);
    return;
  }
  if (Array.isArray(node)) {
    for (const child of node) walkUnknown(child, into, depth + 1);
    return;
  }
  if (typeof node === "object") {
    const rec = node as Record<string, unknown>;
    for (const [k, v] of Object.entries(rec)) {
      addFromText(k, into);
      walkUnknown(v, into, depth + 1);
    }
  }
}

/**
 * Derive locked Lane B overlay ids from report picks / cohort bucket keys / policy trees.
 * Empty when nothing report-related is in play.
 */
export function laneBOverlaysFromPickPolicy(input: {
  picks?: readonly ReportPick[] | null;
  /** Cohort / bucket key e.g. `ma50:above` or slice label. */
  cohortBucketKey?: string | null;
  /** Free-form labels from policy overlay summaries. */
  labels?: readonly string[] | null;
  /** Policy tree / policyNodes blobs. */
  policyNodes?: unknown;
}): TesseraLaneBOverlayId[] {
  const into = new Set<TesseraLaneBOverlayId>();
  if (input.cohortBucketKey) addFromText(input.cohortBucketKey, into);
  for (const label of input.labels ?? []) addFromText(label, into);
  walkUnknown(input.policyNodes, into);
  for (const pick of input.picks ?? []) {
    addFromText(pick.label, into);
    addFromText(pick.kind, into);
    addFromSliceContext(pick.sliceContext, into);
    walkUnknown(pick.policyNodes, into);
    walkUnknown(pick.rule, into);
    walkUnknown(pick.meta, into);
  }
  return Array.from(into);
}

/** True when chart candle TF differs from the report/sim locked TF. */
export function isReportCandleTfDiverged(
  chartCandleType: string | null | undefined,
  reportCandleType: string | null | undefined
): boolean {
  const a = String(chartCandleType ?? "")
    .trim()
    .toLowerCase()
    .replace(/^1m$/, "m")
    .replace(/^1h$/, "h");
  const b = String(reportCandleType ?? "")
    .trim()
    .toLowerCase()
    .replace(/^1m$/, "m")
    .replace(/^1h$/, "h");
  if (!a || !b) return false;
  return a !== b;
}
